import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '萝莉次元',
  url: 'https://lolidimension.com',
  description: '专注于二次元萝莉文化的创作与分享平台。',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'contact@lolidimension.com',
    availableLanguage: ['Chinese'],
  },
};

const aboutPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 萝莉次元',
  description: '了解萝莉次元的品牌故事、社区理念、发展历程与联系方式。',
  url: 'https://lolidimension.com/about',
};

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rifcnnahb7382ovn30', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const teamMembers = [
    {
      name: '樱花兔',
      role: '创始人 & 主编辑',
      description: '十年二次元文化爱好者，致力于打造最温暖的萝莉文化社区。',
    },
    {
      name: '星之绘',
      role: '首席插画师',
      description: '职业插画师，擅长日系萝莉角色设计，作品多次入选Pixiv日榜。',
    },
    {
      name: '东方绘卷',
      role: '内容总监',
      description: '资深动漫评论人，国风二次元融合的积极推动者。',
    },
    {
      name: '暗夜绘师',
      role: '社区运营',
      description: '五年社区运营经验，热衷于连接每一位热爱萝莉文化的创作者。',
    },
  ];

  const milestones = [
    { year: '2024', event: '萝莉次元正式上线，开启二次元萝莉文化创作分享之旅' },
    { year: '2025', event: '社区注册用户突破10万，举办首届"萌之祭"角色设计大赛' },
    { year: '2026', event: '内容总数突破5000篇，与30+知名画师达成合作，开启品牌升级' },
  ];

  return (
    <>
      <JsonLd data={organizationSchema} />
      <JsonLd data={aboutPageSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Brand Intro Hero */}
        <section className="relative w-full py-16 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20and%20dreamy%20anime%20style%20illustration%20with%20soft%20pastel%20pink%20background%2C%20floating%20heart%20shapes%20and%20stars%2C%20gentle%20magical%20atmosphere%2C%20community%20theme%2C%20welcoming%20vibes%2C%20ethereal%20quality%2C%20no%20characters&width=1800&height=700&seq=about-hero-loli&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          <div className="relative z-10 max-w-3xl mx-auto text-center px-4">
            <h1 className="font-heading text-3xl md:text-5xl text-white mb-4">
              关于萝莉次元
            </h1>
            <p className="text-white/80 text-sm md:text-lg leading-relaxed">
              萝莉次元诞生于对二次元萝莉文化的热爱。我们相信每一个可爱的角色、每一幅精致的插画、每一个用心的设定，都值得被看见、被分享、被珍藏。在这里，创作者与爱好者相聚，共同编织属于萝莉次元的无限可能。
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading text-2xl text-foreground-900 mb-4">
                我们的故事
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                萝莉次元创立于2024年，由一群热爱二次元萝莉文化的创作者发起。创始人"樱花兔"在多年的同人创作中发现，国内缺少一个专注于高质量萝莉文化内容的分享平台。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                于是，萝莉次元应运而生。从最初的几十位核心画师，到如今拥有超过10万注册用户的活跃社区，我们始终坚守着对品质的追求——每一幅插画、每一个角色设定、每一篇创作教程，都经过精心的审核与筛选。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                我们致力于打造一个温暖、包容、高品质的二次元萝莉文化社区。在这里，无论是资深画师还是入门新手，都能找到属于自己的位置和成长空间。
              </p>
            </div>
            <div className="relative w-full h-64 md:h-80 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Anime%20style%20illustration%20of%20a%20cozy%20creative%20studio%20with%20art%20supplies%2C%20sketchbooks%2C%20cute%20decorations%2C%20warm%20lighting%2C%20creative%20community%20vibes%2C%20soft%20pastel%20colors%2C%20kawaii%20aesthetic&width=800&height=600&seq=about-story-loli&orientation=landscape"
                alt="萝莉次元创作空间"
                title="萝莉次元 - 创作与分享的温暖空间"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl text-foreground-900 mb-3">
                核心团队
              </h2>
              <p className="text-sm text-foreground-600">
                一群热爱二次元萝莉文化的伙伴
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {teamMembers.map((member) => (
                <div
                  key={member.name}
                  className="bg-background-50 rounded-lg p-5 border border-background-200/70 text-center"
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-primary-600 font-heading text-lg">{member.name[0]}</span>
                  </div>
                  <h3 className="font-heading text-sm text-foreground-900 mb-1">{member.name}</h3>
                  <p className="text-xs text-primary-600 mb-2">{member.role}</p>
                  <p className="text-xs text-foreground-500 leading-relaxed">{member.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="w-full max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl text-foreground-900 mb-3">
              发展历程
            </h2>
            <p className="text-sm text-foreground-600">
              每一年的成长，都离不开你的陪伴
            </p>
          </div>
          <div className="max-w-2xl mx-auto">
            {milestones.map((milestone, index) => (
              <div key={milestone.year} className="flex gap-6 pb-8 last:pb-0">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {milestone.year.slice(2)}
                  </div>
                  {index < milestones.length - 1 && (
                    <div className="w-0.5 flex-1 bg-primary-200 mt-2"></div>
                  )}
                </div>
                <div className="pt-2">
                  <h3 className="font-heading text-base text-foreground-900 mb-1">{milestone.year}</h3>
                  <p className="text-sm text-foreground-600">{milestone.event}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Reputation / Stats */}
        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="font-heading text-3xl text-accent-500 mb-1">10万+</div>
                <p className="text-sm text-foreground-600">注册用户</p>
              </div>
              <div>
                <div className="font-heading text-3xl text-accent-500 mb-1">5000+</div>
                <p className="text-sm text-foreground-600">优质内容</p>
              </div>
              <div>
                <div className="font-heading text-3xl text-accent-500 mb-1">30+</div>
                <p className="text-sm text-foreground-600">合作画师</p>
              </div>
              <div>
                <div className="font-heading text-3xl text-accent-500 mb-1">98%</div>
                <p className="text-sm text-foreground-600">用户好评率</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="w-full max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl text-foreground-900 mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600">
              有任何问题或合作意向，欢迎随时与我们联系
            </p>
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
            <form onSubmit={handleSubmit} data-readdy-form="">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm text-foreground-700 mb-1.5">
                  主题 <span className="text-primary-500">*</span>
                </label>
                <select
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors cursor-pointer"
                >
                  <option value="">请选择主题</option>
                  <option value="合作咨询">合作咨询</option>
                  <option value="投稿相关">投稿相关</option>
                  <option value="问题反馈">问题反馈</option>
                  <option value="商务合作">商务合作</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div className="mb-4">
                <label htmlFor="message" className="block text-sm text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                  placeholder="请输入您的留言内容（500字以内）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              {/* Honeypot */}
              <div className="contact-alt-wrapper">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {formStatus === 'success' && (
                <div className="mb-4 p-3 bg-primary-50 text-primary-700 text-sm rounded-lg border border-primary-200">
                  感谢您的留言！我们会在1-2个工作日内回复您。
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                className="w-full sm:w-auto px-8 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                发送留言
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
            <div className="flex items-center gap-3 p-4 bg-background-100 rounded-lg">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                <i className="ri-mail-line text-lg"></i>
              </div>
              <div>
                <p className="text-xs text-foreground-500">电子邮箱</p>
                <p className="text-sm text-foreground-800">contact@lolidimension.com</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-background-100 rounded-lg">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                <i className="ri-bilibili-line text-lg"></i>
              </div>
              <div>
                <p className="text-xs text-foreground-500">B站官方号</p>
                <p className="text-sm text-foreground-800">萝莉次元Official</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-background-100 rounded-lg">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                <i className="ri-weibo-line text-lg"></i>
              </div>
              <div>
                <p className="text-xs text-foreground-500">微博</p>
                <p className="text-sm text-foreground-800">@萝莉次元</p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}